import filter from 'lodash/filter.js';
import find from 'lodash/find.js';
import flatMap from 'lodash/flatMap.js';
import get from 'lodash/get.js';
import intersection from 'lodash/intersection.js';
import kebabCase from 'lodash/kebabCase.js';
import map from 'lodash/map.js';
import unionBy from 'lodash/unionBy.js';

import { initials } from '@/Helpers.js';
import Course from '@/models/Course.js';
import CourseEnrollment from '@/models/CourseEnrollment.js';
import Model from '@/models/Model.js';
import { ADMIN, INSTRUCTION_ADMIN, INSTRUCTOR, OWNER, STUDENT_SUPPORT } from '@/models/Role.js';
import UserGroup from '@/models/UserGroup.js';
import BasePolicy from '@/policies/BasePolicy.js';
import Cache from '@/utilities/Cache.js';

export default class User extends Model {
    static entity = 'users';

    static modelName = 'User';

    static searchable() {
        return ['first_name', 'last_name', 'email', 'external_id'];
    }

    static fields() {
        return {
            id: this.attr(null),
            external_id: this.attr(null),
            first_name: this.string(''),
            last_name: this.string(''),
            email: this.string(null),

            must_change_password: this.boolean(false),
            photo_url: this.attr(null),
            theme_preference: this.string(null),
            dark_theme_preference: this.string(null),
            quiz_theme_adjustment: this.string(null),
            quiz_active_id: this.attr(null),
            quiz_checkin_id: this.attr(null),
            service_account: this.boolean(false),
            remember_token: this.attr(null),
            deleted_at: this.attr(null),
            created_at: this.attr(null),
            updated_at: this.attr(null),
            last_active_at: this.attr(null),
            last_login_at: this.attr(null),
            identity_verification_active_id: this.attr(null),
            identity_verification_attempt_active_id: this.attr(null),
            course_key_id: this.attr(null),
            pivot: this.attr(null),
            risk_statuses: this.attr(null),
            quiz_modal: this.attr(null),
            activity_submissions: this.attr(null),
            phone_number: this.attr(null),
            sms_notifications_enabled: this.attr(null),
            sms_due_date_notifications: this.attr(null),
            sms_course_announcement_notifications: this.attr(null),
            sms_messages_notifications: this.attr(null),
            sms_activity_updates_notifications: this.attr(null),
            campus_groups: this.attr(null),
            active_quiz: this.attr(null),
            email_notifications_enabled: this.attr(null),
            email_due_date_notifications: this.attr(null),
            email_activity_updates_notifications: this.attr(null),
            email_course_announcement_notifications: this.attr(null),
            email_messages_notifications: this.attr(null),
            push_notifications_enabled: this.attr(null),
            push_due_date_notifications: this.attr(null),
            push_course_announcement_notifications: this.attr(null),
            push_activity_updates_notifications: this.attr(null),
            push_messages_notifications: this.attr(null),
            features: this.attr(null),

            ...super.fields(),

            roles: this.morphToMany('roles', 'model_has_roles', 'role_id', 'model_id', 'model_type'),
            permissions: this.morphToMany(
                'permissions',
                'model_has_permissions',
                'permission_id',
                'model_id',
                'model_type'
            ),
            user_groups: this.hasMany('user_groups', 'user_id'),
            user_groups_with_groups_without_course_ids: this.attr(null),

            discussion_threads: this.hasMany('discussion_threads', 'user_id'),
            meeting_users: this.hasMany('meeting_users', 'user_id'),
            enrollments: this.hasMany('course_enrollments', 'user_id'),
            settings: this.hasMany('user_settings', 'user_id'),
            visible_enrollments: this.attr(null),
            statuses: this.morphMany('statuses', 'model_id', 'model_type_entity'),
        };
    }

    get name() {
        return `${this.first_name} ${this.last_name}`;
    }

    get is_admin() {
        return this.hasAnyRole(['Owner', 'Admin', 'Instruction Admin']);
    }

    get is_super_admin() {
        return this.hasAnyRole(['Owner', 'Admin']);
    }

    get is_faculty() {
        return this.hasAnyRole([
            'Owner',
            'Admin',
            'Instruction Admin',
            'Instructor',
            'Student Support',
            'Content Editor',
        ]);
    }

    get is_instructor() {
        return this.hasAnyRole([
            'Owner',
            'Admin',
            'Instructor',
            'Instruction Admin',
            'Student Support',
            'Course Instructor',
        ]);
    }

    get all_permissions() {
        return Cache.remember(`user:${this.id}:all-permissions`, () => {
            const roles = this.roles || [];
            const permissions = this.permissions || [];

            return unionBy([...flatMap(roles, role => role.permissions), ...permissions], 'id');
        });
    }

    get initials() {
        return initials(this.first_name, this.last_name);
    }

    meetingUserAccount(provider) {
        return find(this.meeting_users, meetingUser => meetingUser.provider === provider);
    }

    enrollmentFor(courseId, roleName = null) {
        return find(this.enrollments, enrollment => {
            if (enrollment.course_id !== parseInt(courseId, 10)) return false;

            if (!roleName) return enrollment;

            return find(enrollment.roles, role => role.name === roleName);
        });
    }

    hasAnyRole(roleNames) {
        return map(this.roles, 'name').some(role => roleNames.includes(role));
    }

    hasAnyRoleViaGroup(roleNames) {
        return map(this.user_groups, 'role.name').some(role => roleNames.includes(role));
    }

    hasRole(roleName) {
        return this.hasAnyRole([roleName]);
    }

    hasRoleViaGroup(roleName) {
        return this.hasAnyRoleViaGroup([roleName]);
    }

    isStudentOf(courseId) {
        return Cache.remember(`user:${this.id}:is-student-of:${courseId}`, () => {
            const student = this.enrollmentFor(courseId, 'Student');

            return !!student && student.user_id === this.id;
        });
    }

    hasNonStudentRoleInCourse(courseId) {
        return Cache.remember(`user:${this.id}:has-non-student-role-in:${courseId}`, () => {
            const enrollment = this.enrollmentFor(courseId);

            return !!enrollment && (enrollment.roles ?? []).some(role => role.name !== 'Student');
        });
    }

    isInstructorOf(courseId) {
        return Cache.remember(`user:${this.id}:is-instructor-of:${courseId}`, () => {
            const instructor = this.enrollmentFor(courseId, 'Instructor');
            return !!instructor && instructor.user_id === this.id;
        });
    }

    hasRoleWithinActiveCourse(role) {
        return Cache.remember(`user:${this.id}:is-${kebabCase(role)}-of-active-course`, () => {
            return (
                this.enrollments.filter(enrollment => {
                    const course = Course.find(enrollment.course_id);

                    if (!course?.in_progress || enrollment.has_disabled_status || enrollment.has_removed_status) {
                        return false;
                    }

                    // TODO: refactor for ESLINT
                    // eslint-disable-next-line no-shadow
                    return enrollment.roles.map(role => role.name || []).includes(role);
                }).length > 0
            );
        });
    }

    hasActiveFeature(featureName) {
        return this.features?.includes(featureName);
    }

    isInstructorOfActiveCourse() {
        return this.hasRoleWithinActiveCourse('Instructor');
    }

    isStudentOfActiveCourse() {
        return this.hasRoleWithinActiveCourse('Student');
    }

    hasGroupPermissionWithinCourse(course, permission) {
        const courseId = typeof course === 'object' ? course.id : course;
        return Cache.remember(`user:${this.id}:has-group-permission-${permission}-within-course:${courseId}`, () => {
            return this.userGroupsAssociatedWithCourse(course)
                .map(userGroup => userGroup.hasPermissionTo(permission))
                .includes(true);
        });
    }

    hasGroupPermissionWithinCourseTemplate(courseTemplate, permission) {
        const courseTemplateGroupIds = courseTemplate.groups.map(group => group.id);

        const userGroups = this.user_groups.filter(userGroup => courseTemplateGroupIds.includes(userGroup.group_id));

        return userGroups.map(userGroup => userGroup.hasPermissionTo(permission)).includes(true);
    }

    hasRoleWithinCourseGroup(course, roleName) {
        const userGroups = this.userGroupsAssociatedWithCourse(course);
        const result = userGroups.map(userGroup => userGroup.role.name === roleName).includes(true);

        return result;
    }

    hasGroupPermissionWithinNonActiveCourse(course, permission) {
        return this.userGroupsAssociatedWithCourse(course)
            .map(userGroup => userGroup.hasPermissionTo(permission) && !course.in_progress)
            .includes(true);
    }

    userGroupsAssociatedWithCourse(course) {
        const courseId = typeof course === 'object' ? course.id : course;

        const filteredGroups = UserGroup.query()
            .with(['role', 'group.courses'])
            .where('user_id', this.id)
            .whereHas('group', group =>
                group.whereHas('courses', courseGroup => courseGroup.where('course_id', courseId))
            )
            .get();

        return filteredGroups;
    }

    // userGroupsAssociatedWithQuiz(quiz) {
    //     const quizId = typeof quiz === 'object' ? quiz.id : quiz;
    //
    //     return UserGroup.query()
    //         .with(['role', 'group'])
    //         .where('user_id', this.id)
    //         .whereHas('group', query => query.where('quiz_ids', quiz_ids => quiz_ids.includes(quizId)))
    //         .get();
    // }

    hasPermissionTo(permission) {
        return !!find(this.all_permissions, { name: permission });
    }

    hasGroupPermissionTo(permission) {
        return Cache.remember(`user:${this.id}:has-group-permission-to:${permission}`, () => {
            return UserGroup.query()
                .with('role.permissions')
                .whereHas('role', query =>
                    query.whereHas('permissions', permissionQuery => permissionQuery.where('name', permission))
                )
                .exists();
        });
    }

    /**
     * Checks if the user has a given permission within specified groups.
     * @param {number[]} groups - An array of group IDs to check.
     * @param {string} permission - The permission to check for.
     * @returns {boolean} Whether the user has the permission in any of the groups.
     */
    hasPermissionWithinGroups(groupIds, permission) {
        if (this.is_admin) return true;

        const userGroupIds = this.user_groups
            .filter(userGroup => groupIds.includes(userGroup.group_id))
            .map(userGroup => userGroup.group_id);

        const userGroups = UserGroup.query().whereIdIn('group_id', userGroupIds).get();

        return userGroups.some(userGroup => userGroup.hasPermissionTo(permission));
    }

    hasEnrolledPermissionTo(permission) {
        return Cache.remember(`user:${this.id}:has-enrolled-permission-to:${permission}`, () => {
            return CourseEnrollment.query()
                .with('roles.permissions')
                .whereHas('roles', query =>
                    query.whereHas('permissions', permissionQuery => permissionQuery.where('name', permission))
                )
                .exists();
        });
    }

    canAccessReportsPage() {
        return map(this.roles, 'id').some(roleId => [OWNER, ADMIN, INSTRUCTION_ADMIN, INSTRUCTOR].includes(roleId));
    }

    canAccessAllReports() {
        return map(this.roles, 'id').some(roleId => [OWNER, ADMIN, INSTRUCTION_ADMIN].includes(roleId));
    }

    hasInstructorRole() {
        return map(this.roles, 'id').includes(INSTRUCTOR);
    }

    isGroupInstructorOf(courseId) {
        return Cache.remember(`user:${this.id}-is-group-instructor-of:${courseId}`, () => {
            const course = Course.query().with('groups').find(courseId);

            if (!course) return false;

            const instructorGroups = this.user_groups.filter(userGroup => userGroup.role_id === INSTRUCTOR);

            return course.groups
                .map(courseGroup =>
                    instructorGroups.map(instructorGroup => instructorGroup.group_id === courseGroup.id).includes(true)
                )
                .includes(true);
        });
    }

    isGroupInstructor() {
        return Cache.remember(`user:${this.id}-is-group-instructor`, () => {
            return this.user_groups.filter(userGroup => userGroup.role_id === INSTRUCTOR).length > 0;
        });
    }

    isStudentSupport() {
        return Cache.remember(
            `user:${this.id}:is-student-support`,
            () => this.hasRole('Student Support') || this.hasRoleViaGroup('Student Support')
        );
    }

    isNotStudentSupport() {
        return !this.isStudentSupport();
    }

    isStudentSupportOfCourse(courseId) {
        return Cache.remember(
            `user:${this.id}:is-student-support-of-course:${courseId}`,
            () => this.isStudentSupport() || this.isGroupStudentSupportOf(courseId)
        );
    }

    isNotStudentSupportOfCourse(courseId) {
        return !this.isStudentSupportOfCourse(courseId);
    }

    isGroupStudentSupportOf(courseId) {
        return Cache.remember(`user:${this.id}:is-group-student-support-of:${courseId}`, () => {
            const course = Course.query().with('groups').find(courseId);

            if (!course) return false;

            const studentSupportGroups = this.user_groups.filter(userGroup => userGroup.role_id === STUDENT_SUPPORT);

            if (studentSupportGroups.length === 0) return false;

            if (course.groups.length === 0) return false;

            return course.groups
                .map(courseGroup =>
                    studentSupportGroups
                        .map(studentSupportGroup => studentSupportGroup.group_id === courseGroup.id)
                        .includes(true)
                )
                .includes(true);
        });
    }

    hasPermissionViaCourseTo(course, permission) {
        return (
            this.hasUnlimitedScopePermissionTo(permission) ||
            this.hasLimitedScopePermissionViaCourseTo(course, permission) ||
            this.isEnrolledWithPermission(course, permission) ||
            this.hasGroupPermissionWithinCourse(course, permission)
        );
    }

    hasUnlimitedScopePermissionTo(permission) {
        return Cache.remember(`user:${this.id}:has-unlimited-scope-permission-to:${permission}`, () => {
            return this.roles
                .map(role => {
                    if (!role.hasPermissionTo(permission)) {
                        return false;
                    }

                    const userGroups = UserGroup.query().where('user_id', this.id).get();

                    if (userGroups.length === 0) {
                        return role.name === INSTRUCTION_ADMIN;
                    }

                    const hasUserGroupWithoutRole =
                        filter(userGroups, userGroup => !userGroup.role_id && !!userGroup.group_id).length > 0;

                    const doesntHaveUserGroupAssignedWithRole =
                        filter(userGroups, userGroup => userGroup.role_id === role.id && !!userGroup.group_id)
                            .length === 0;

                    return hasUserGroupWithoutRole && doesntHaveUserGroupAssignedWithRole;
                })
                .includes(true);
        });
    }

    hasLimitedScopePermissionViaCourseTo(course, permission) {
        if (!course) {
            return false;
        }

        return Cache.remember(
            `user:${this.id}:has-limited-scope-permission-via-course-to:${course.id}:${permission}`,
            () => {
                const groups = get(Course.query().with('groups.users.role.permissions').find(course.id), 'groups', []);

                return flatMap(groups, group =>
                    group.users
                        .filter(userGroup => userGroup.user_id === this.id)
                        .map(userGroup => {
                            if (!userGroup.role) {
                                return true;
                            }

                            if (BasePolicy.permissionStartsWith(permission, 'view')) {
                                return userGroup.role.hasPermissionTo(permission);
                            }

                            return userGroup.role.hasPermissionTo(permission) && !course.is_accessible_to_students;
                        })
                ).includes(true);
            }
        );
    }

    isEnrolledWithPermission(course, permission) {
        if (!course) {
            return false;
        }

        return Cache.remember(`user:${this.id}:is-enrolled-with-permission:${course.id}:${permission}`, () => {
            const enrollment = CourseEnrollment.query()
                .with('roles.permissions')
                .where('course_id', course.id)
                .where('user_id', this.id)
                .first();

            if (!enrollment) {
                return false;
            }

            return enrollment.hasPermissionTo(permission);
        });
    }

    isInAssetGroup(asset) {
        const userGroupIds = this.user_groups.map(userGroup => userGroup.group_id);

        const assetGroupIds = asset.groups.map(group => group.id);

        return !!intersection(userGroupIds, assetGroupIds).length;
    }
}
